import { ChipModule } from 'primeng/chip';
import { ConventionalInfo } from './../interfaces/conventionalinfo.interface';
import { TranslocoService } from '@jsverse/transloco';
import {
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { Confirmation, FilterMetadata, MessageService } from 'primeng/api';
import { DateFormat } from 'src/enums/dateformat.enum';
import { COOKIE_LANG_KEY, APP_LANGS } from './constants';
import { ConventionalInfoDataType } from 'src/enums/conventionalinfodatatype.enum';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { FileUploadModule } from 'primeng/fileupload';
import { InplaceModule } from 'primeng/inplace';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { KeyFilterModule } from 'primeng/keyfilter';
import { KnobModule } from 'primeng/knob';
import { ListboxModule } from 'primeng/listbox';
import { MenuModule } from 'primeng/menu';
import { MenubarModule } from 'primeng/menubar';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { PanelModule } from 'primeng/panel';
import { PopoverModule } from 'primeng/popover';
import { ProgressBarModule } from 'primeng/progressbar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { RippleModule } from 'primeng/ripple';
import { ScrollTopModule } from 'primeng/scrolltop';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SliderModule } from 'primeng/slider';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';
import { ApiResponse } from 'src/interfaces/apiresponse.interface';
import { Injectable } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';

import { AutoFocusModule } from 'primeng/autofocus';

const StringIsNumber = (value: unknown) => isNaN(Number(value)) === false;

@Injectable()
export default class Helpers {
  private static instance: Helpers;

  private constructor(private translocoService: TranslocoService) {}

  static getInstance(translocoService: TranslocoService): Helpers {
    if (!this.instance) this.instance = new this(translocoService);

    return this.instance;
  }

  getConfirmBase(confirmKey: string = 'defaultConfirm') {
    return {
      key: confirmKey,
      header: this.translocoService.translate('confirm'),
      icon: 'icon-helpQuestion-outline',
      message: '',
      acceptLabel: this.translocoService.translate('yes'),
      rejectLabel: this.translocoService.translate('cancel'),
      acceptIcon: 'icon-hook-outline relative -top-2px',
      rejectIcon: 'icon-close-outline',
      defaultFocus: 'reject',
      blockScroll: false,
    } as Confirmation;
  }

  showApiResponseMessage(
    response: HttpResponse<ApiResponse> | HttpErrorResponse | any,
    messageService: MessageService,
    messageKey: string,
    life: number = 15000
  ): void {
    const data = response.body || response.error || response;

    messageService.add({
      key: messageKey,
      severity: data?.success ? 'success' : 'error',
      summary: data?.summary,
      detail: data?.errors?.join(', '),
      life: life,
    });
  }

  static isIframe() {
    return window !== window.parent && !window.opener;
  }

  static isIE() {
    return (
      window.navigator.userAgent.indexOf('MSIE ') > -1 ||
      window.navigator.userAgent.indexOf('Trident/') > -1
    );
  }

  static clone(obj: object): object {
    return Object.assign({}, obj);
  }

  static getBaseUrlWithPort() {
    return `${location.protocol}//${location.hostname}${
      location.port ? ':' + location.port : ''
    }`;
  }

  static getUserInitials(name: string) {
    if (!name) return '';

    if (name.includes('@')) {
      let namePart = name.split('@')[0];

      return namePart
        .split('.')
        ?.slice(0, 2)
        ?.map((x) => x[0])
        ?.join('')
        ?.toUpperCase();
    }

    return name[0];
  }

  static getIdleTimeout(): number {
    // return 5;
    // return 5 * 60 - 30; // for testing
    return 15 * 60 - 30;
  }

  static getStringOrNull(v: string) {
    return v ? v?.trim() : '';
  }

  static isValueEmpty(v: any) {
    return v === '' || v === null || v === undefined || v.length === 0;
  }

  static isAllObjectPropertiesFilled(o?: { [key: string]: any }) {
    return o ? true : Object.values(o!).some((v) => Helpers.isValueEmpty(v));
  }

  static isAllObjectPropertiesEmpty(o?: { [key: string]: any }) {
    return o ? true : Object.values(o!).every((v) => Helpers.isValueEmpty(v));
  }

  static removeEmptyObjectProperties(o: { [key: string]: any }): any {
    Object.keys(o)
      .filter((p: any) => Helpers.isValueEmpty(o[p]))
      .forEach((p) => delete o[p]);

    return o;
  }

  static sortByKey<T, K extends keyof T>(
    arr: T[],
    key: K,
    ascending: boolean = true
  ): T[] {
    if (!arr?.length) return arr;

    arr = arr.sort((a: T, b: T) => (a[key] > b[key] ? 1 : -1));

    return ascending ? arr : arr.reverse();
  }

  static getCookie(cname: string): string {
    let name = cname + '=';
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }

    return '';
  }

  static setCookie(cname: string, cvalue: string, exdays: number = 999) {
    const d = new Date();
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
    let expires = 'expires=' + d.toUTCString();
    document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
  }

  static setConventionalInfoValueByDataType(
    conventionalInfos: ConventionalInfo[]
  ): void {
    conventionalInfos.forEach((conventionalInfo) => {
      const dataType = conventionalInfo.conventionalInfoKey.dataType;

      if (dataType === ConventionalInfoDataType.DateTime) {
        conventionalInfo.value = new Date(
          <Date>(<unknown>conventionalInfo.value)
        );
      } else if (dataType === ConventionalInfoDataType.Numeric) {
        conventionalInfo.value = Helpers.toDecimal(
          <string>conventionalInfo.value
        );
      } else if (dataType === ConventionalInfoDataType.Boolean) {
        conventionalInfo.value = conventionalInfo.value !== 'False';
      }
    });
  }

  static getFilterValueFromLocalStorage(
    key: string,
    filterName: string
  ): any | null {
    const data = localStorage.getItem(key);
    if (!data) return;

    const storedTableSettings = JSON.parse(data);
    if (!storedTableSettings) return;

    if (storedTableSettings.filters) {
      const filterData = storedTableSettings.filters[filterName];

      if (filterData) {
        return filterData.value;
      }
    }
  }

  static removeDuplicates(arr: any[]): any[] {
    return [...new Set(arr)];
  }

  static toDecimal(value: string): number {
    return parseFloat(value);
  }

  static removeExpandedRowKeyFromLocalStorage(key: string): void {
    const data = localStorage.getItem(key);
    if (!data) return;

    const storedTableSettings = JSON.parse(data);
    if (!storedTableSettings) return;

    storedTableSettings.expandedRowKeys = [];
    localStorage.setItem(key, JSON.stringify(storedTableSettings));
  }

  static getPageSizeFromLocalStorage(key: string): number | void {
    const data = localStorage.getItem(key);
    if (!data) return;

    const storedTableSettings = JSON.parse(data);
    if (!storedTableSettings) return;

    return storedTableSettings.rows;
  }

  static getOrderByFromLocalStorage(key: string): string | undefined {
    const data = localStorage.getItem(key);
    if (!data) return undefined;

    const storedTableSettings = JSON.parse(data);
    if (!storedTableSettings) return undefined;

    return storedTableSettings.sortField;
  }

  static getOrderByDirectionFromLocalStorage(key: string): number | undefined {
    const data = localStorage.getItem(key);
    if (!data) return undefined;

    const storedTableSettings = JSON.parse(data);
    if (!storedTableSettings) return undefined;

    return storedTableSettings.sortOrder;
  }

  static getOrderByFromEvent(sortField?: string, sortOrder?: number): string {
    if (!sortField) return '';

    return `${sortOrder === -1 ? '-' : ''}${sortField}`;
  }

  static getPageFromEvent(first?: number, rows?: number): number {
    return !first ? 1 : Math.floor(first + rows!) / rows!;
  }

  static getFilterFromEvent(eventFilter?: {
    [s: string]: FilterMetadata;
  }): string {
    if (!eventFilter) return '';

    let filters = [];

    for (const [key, filterMeta] of Object.entries(eventFilter)) {
      if (key === 'undefined') continue;
      if (!filterMeta.value) continue;

      let operator = '';
      let matchMode = (filterMeta as any).matchMode;
      let value = (filterMeta as any).value;

      if (matchMode.substr(0, 4) === 'in') {
        let values = value.map((x: any) => x.name).join('|');
        filters.push(`${key}==${values}`);
      } else if (matchMode === 'startsWith') operator = '_=*';
      // else if (matchMode === "equals" && typeof(value) === "string") operator = "==*"; // issue with enum keys
      else if (matchMode === 'equals') operator = '==';
      else if (matchMode === 'notEquals' && typeof value === 'string')
        operator = '!=*';
      else if (matchMode === 'notEquals') operator = '!=';
      else if (matchMode === 'contains') operator = '@=*';
      else if (matchMode === 'notContains') operator = '!@=*';
      else if (matchMode === 'gt') operator = '>';
      else if (matchMode === 'gte') operator = '>=';
      else if (matchMode === 'lt') operator = '<';
      else if (matchMode === 'lte') operator = '<=';
      else if (matchMode.substr(0, 4) === 'date') {
        value = Helpers.formatDate(value, DateFormat.ISO8061);

        if (matchMode === 'dateBefore') operator = '<';
        else {
          let day = value;
          let tmpDate = new Date(value);
          tmpDate.setDate(tmpDate.getDate() + 1);
          let dayEnd = Helpers.formatDate(tmpDate, DateFormat.ISO8061);

          if (matchMode === 'dateIs') {
            filters.push(`${key}>=${day},${key}<=${dayEnd}`);
          } else if (matchMode === 'dateAfter') {
            filters.push(`${key}>${dayEnd}`);
          }
          // else if (matchMode === "dateIsNot") {
          //   // not working
          //   filters.push(`(${key}<${day}|${key}>${dayEnd})`);
          // }
        }
      }

      if (operator) {
        filters.push(`${key}${operator}${value}`);
      }
    }

    return filters.join(',');
  }

  static clearBrowserStorage() {
    sessionStorage.removeItem('idam');
    Helpers.removeLocalStorageWithPrefix('ng2Idle');
  }

  static removeLocalStorageWithPrefix(prefix: string): void {
    Object.entries(localStorage)
      .map((x) => x[0])
      .filter((x) => x.startsWith(prefix))
      .map((x) => localStorage.removeItem(x));
  }

  static getShortLangCode(langCode: string): string {
    let shortLangCode: string;

    switch (langCode) {
      case 'cs-CZ':
        shortLangCode = 'cz';
        break;
      default:
        shortLangCode = langCode.substr(0, 2);
        break;
    }

    return shortLangCode;
  }

  static formatDate(date: Date, format: DateFormat): string {
    let d = new Date(date),
      year = d.getFullYear(),
      month = d.getMonth() + 1,
      day = d.getDate(),
      hour = d.getHours(),
      minute = d.getMinutes(),
      second = d.getSeconds(),
      formattedDate: string;

    if (format === DateFormat.ISO8061) {
      let y = year.toString(),
        M = this.padLeftDatePart(month),
        d = this.padLeftDatePart(day);

      formattedDate = [y, M, d].join('-');
    } else if (format === DateFormat.fileName) {
      let y = year.toString(),
        M = this.padLeftDatePart(month),
        d = this.padLeftDatePart(day),
        h = this.padLeftDatePart(hour),
        m = this.padLeftDatePart(minute),
        s = this.padLeftDatePart(second);

      formattedDate = [y, M, d, h, m, s].join('-');
    } else {
      formattedDate = date.toDateString();
    }

    return formattedDate;
  }

  static formatDateTimeRangeForQuery(
    dateTimeRange?: Date[],
    dateFormat: DateFormat = DateFormat.ISO8061
  ): { [key: string]: string | null } | null {
    if (!dateTimeRange) return null;

    return {
      from: dateTimeRange[0]
        ? Helpers.formatDate(dateTimeRange[0], dateFormat)
        : null,
      to: dateTimeRange[1]
        ? Helpers.formatDate(dateTimeRange[1], dateFormat)
        : null,
    };
  }

  static convertUTCDateToLocalDate(date: Date) {
    const newDate = new Date(date);
    newDate.setMinutes(date.getMinutes() - date.getTimezoneOffset());

    return newDate;
  }

  static formatNumberRangeForQuery(
    numberRange?: number[]
  ): { [key: string]: number | null } | null {
    if (!numberRange) return null;

    return {
      from: numberRange[0] ? numberRange[0] : null,
      to: numberRange[1] ? numberRange[1] : null,
    };
  }

  static getKeyValue =
    <T extends object, U extends keyof T>(key: U) =>
    (obj: T) =>
      obj[key];

  static isEmptyObject(obj: any): boolean {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
  }

  //     static toUpperCaseFirst(str: string) {
  //         return str[0].toUpperCase() + str.slice(1);
  //     }

  //     static GetFilenameFormat(name: string) {
  //         let datePart = this.formatDate(new Date(), DateFormat.fileName);

  //         return `PwC-Online-Invoice-${name}_${datePart}`;
  //     }

  static toLowerCaseFirst(str: string): string {
    if (!str || !str.length) {
      return str;
    }

    return str[0]?.toLocaleLowerCase() + str?.slice(1);
  }

  static toUpperCaseFirst(str: string): string {
    if (!str || !str.length) {
      return str;
    }

    return str[0]?.toLocaleUpperCase() + str?.slice(1);
  }

  static toSnakeCase(str: string) {
    return str
      .split(/(?=[A-Z])/)
      .join('_')
      .toLocaleLowerCase();
  }

  static nameof<T>(key: keyof T, instance?: T): keyof T {
    return key;
  }

  static scrollWindowTo(top: number = 0): void {
    window.scrollTo({
      top: top,
      behavior: 'smooth',
    });
  }

  static scrollContentAreaToTop(selector: string = 'main'): void {
    let contentArea = document.querySelector(selector);

    if (contentArea) contentArea.scrollTop = 0;
  }

  static scrollIntoView(selector: string = 'main'): void {
    let contentArea = document.querySelector(selector);

    if (contentArea) contentArea.scrollIntoView();
  }

  static replaceAll(str: string, search: string, replace: string) {
    return str.split(search).join(replace);
  }

  //     static getQueryParameterKeyFromValue(value: string) {
  //         return Object.keys(Object(QueryParameterName)).find(key => Object(QueryParameterName)[key] === value);
  //     }

  //     static scrollContentAreaToTop() {
  //         let contentArea = document.querySelector('main');

  //         if (contentArea) {
  //             contentArea.scrollTop = 0;
  //         }
  //     }

  static getTotalFromHeader(res: any) {
    return res && res.headers
      ? parseInt(res.headers.get('x-total-count') || 0)
      : 0;
  }

  //     static addDaysToDate(days: number, date: Date = new Date()) {
  //         var result = new Date(date);
  //         result.setDate(result.getDate() + days);

  //         return result;
  //     }

  //     static renameProp = (
  //         oldProp,
  //         newProp,
  //         {[oldProp]: old, ...others}
  //     ) => {
  //         return {
  //             [newProp]: old,
  //             ...others
  //         };
  //     };

  // static enumToArray(srcEnum: any) {
  //     return Object.keys(srcEnum)
  //         .filter(StringIsNumber)
  //         .map(key => srcEnum[key]);
  // }

  static enumToArray(srcEnum: any) {
    return Object.keys(srcEnum)
      .filter(StringIsNumber)
      .map((key) => ({ key: +key, name: srcEnum[key] }));
  }

  //     static StringEnumToAssociativeArray(srcEnum: any, sort: boolean = false) {
  //         var arr = Array.from(Object.entries(srcEnum).filter(f => isNaN(Number(f))));

  //         return !sort ? arr : arr.sort(function (a, b) {
  //             return a[1] > b[1] ? 1 : -1;
  //         });
  //     }

  static getDefaultHttpOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods':
          'GET, POST, PATCH, PUT, DELETE, OPTIONS',
        'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
      }),
      observe: 'response' as 'response',
      params: null,
    };
  }

  static trimObjectProperties(obj: any): any {
    return Object.keys(obj).map((k) => {
      if (typeof obj[k] === 'string') {
        obj[k] = obj[k].trim();
      }
    });
  }

  static shallowEqual(object1: any, object2: any) {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (let key of keys1) {
      if (object1[key] !== object2[key]) {
        return false;
      }
    }

    return true;
  }

  static deepClone(obj: any, hash = new WeakMap()): any {
    if (Object(obj) !== obj) return obj; // primitives
    if (hash.has(obj)) return hash.get(obj); // cyclic reference

    const result =
      obj instanceof Set
        ? new Set(obj) // See note about this!
        : obj instanceof Map
        ? new Map(
            Array.from(obj, ([key, val]) => [key, Helpers.deepClone(val, hash)])
          )
        : obj instanceof Date
        ? new Date(obj)
        : obj instanceof RegExp
        ? new RegExp(obj.source, obj.flags)
        : // ... add here any specific treatment for other classes ...
        // and finally a catch-all:
        obj.constructor
        ? new obj.constructor()
        : Object.create(null);
    hash.set(obj, result);

    return Object.assign(
      result,
      ...Object.keys(obj).map((key) => ({
        [key]: Helpers.deepClone(obj[key], hash),
      }))
    );
  }

  static deepEqual(x?: any, y?: any) {
    if (x === y) {
      return true;
    }

    if (
      typeof x == 'object' &&
      x != null &&
      typeof y == 'object' &&
      y != null
    ) {
      if (Object.keys(x).length != Object.keys(y).length) {
        return false;
      }

      if (
        typeof x.getMonth === 'function' &&
        typeof y.getMonth === 'function'
      ) {
        return x.getTime() === y.getTime();
      }

      for (var prop in x) {
        if (y.hasOwnProperty(prop)) {
          if (!Helpers.deepEqual(x[prop], y[prop])) {
            return false;
          }
        } else {
          return false;
        }
      }

      return true;
    } else {
      return false;
    }
  }

  static isObject(object: any) {
    return object != null && typeof object === 'object';
  }

  static convertToASCII(str: string): string {
    if (!str) return str;
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  static getSavedLangOrDefault(): string {
    let cookieValue: string = Helpers.getCookie(COOKIE_LANG_KEY);
    let savedLang: string = cookieValue.slice(-5);

    return savedLang || APP_LANGS[0];
  }

  private static padLeftDatePart(value: number) {
    let str = value.toString();

    return str.length < 2 ? '0' + str : str;
  }

  static removeLastCharacterFromStringIf(str: string, char: string): string {
    if (str.substr(str.length - 1) === char) {
      str = str.substr(0, str.length - 1);
    }

    return str;
  }

  static primeNgModules: any[] = [
    BlockUIModule,
    AutoCompleteModule,
    ButtonModule,
    DatePickerModule,
    CardModule,
    CheckboxModule,
    ChipModule,
    ConfirmDialogModule,
    ConfirmPopupModule,
    DialogModule,
    SelectModule,
    DynamicDialogModule,
    FileUploadModule,
    InplaceModule,
    InputMaskModule,
    InputNumberModule,
    ToggleSwitchModule,
    TextareaModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    KeyFilterModule,
    KnobModule,
    ListboxModule,
    MenubarModule,
    MenuModule,
    MessageModule,
    MultiSelectModule,
    SelectModule,
    PopoverModule,
    ProgressBarModule,
    PanelModule,
    RadioButtonModule,
    RippleModule,
    ScrollTopModule,
    SelectButtonModule,
    DrawerModule,
    SliderModule,
    SplitButtonModule,
    TableModule,
    TabsModule,
    ToastModule,
    ToggleButtonModule,
    ToolbarModule,
    TooltipModule,
    TreeModule,
    SkeletonModule,
    AutoFocusModule,
  ];
}
